import { ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';
import DialogTitle from '../dialog-title/DialogTitle';

type DeleteConfirmationDialogProps = {
  title: ReactNode;
  description?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  children?: ReactNode;
  isLoading: boolean;
  disabled?: boolean;
  confirmActionText?: string;
};

export const ConfirmationDialog = ({
  title,
  description,
  onConfirm,
  onCancel,
  open,
  children,
  isLoading,
  disabled,
  confirmActionText,
}: DeleteConfirmationDialogProps) => {
  const intl = useIntl();
  return (
    <Dialog
      data-cy="confirmation-dialog"
      open={open}
      onClose={() => onCancel()}
      sx={{ transition: 'smooth' }}
      slotProps={{
        paper: {
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        },
      }}
    >
      <>
        <DialogTitle title={title} onClose={() => onCancel()} />

        <DialogContent sx={{ mx: 4, mt: 4, p: 0 }}>
          <Stack spacing={2} useFlexGap>
            {description ? (
              typeof description === 'string' ? (
                <Typography variant="body2">{description}</Typography>
              ) : (
                description
              )
            ) : null}
            {children}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ pb: 4, pt: 2.5, mr: 4, pr: 0 }}>
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            data-cy="confirmation-dialog-cancel-btn"
            onClick={() => onCancel()}
          >
            {intl.formatMessage({
              id: 'components.confirmationDialog.cancel',
            })}
          </Button>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            data-cy="confirmation-dialog-confirm-btn"
            onClick={() => onConfirm()}
            disabled={disabled || isLoading}
            endIcon={isLoading ? <CircularProgress size={14} /> : null}
          >
            {confirmActionText ??
              intl.formatMessage({
                id: 'components.confirmationDialog.confirm',
              })}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};
